import { Link } from 'react-router-dom';
import { Info } from 'lucide-react';

/**
 * El nombre —o el alojamiento— de quien llamó.
 *
 * Hay dos formas de saberlo y no valen lo mismo. Si su número se reconoce en la
 * base de huéspedes, es un dato: sale tal cual, y enlazado a su ficha. Si no se
 * reconoce, lo único que hay es lo que
 * la persona dijo de sí misma al descolgar, que el análisis saca de la
 * transcripción. Eso puede fallar —un nombre mal transcrito, un pariente que
 * llama por el titular, una confusión del modelo—, así que se enseña en
 * cursiva y con un icono que lo explica al pasar por encima.
 *
 * Esta distinción no es un adorno. Alguien que vea «Marta García» en el CRM y
 * llame llamándola así tiene que poder saber si eso lo dijo el sistema o lo
 * dijo ella por teléfono.
 */
export default function NombreOido({
  confirmado,
  oido,
  vacio = 'sin identificar',
  ficha,
}: {
  /** El de la base de huéspedes, si el teléfono se reconoce. */
  confirmado?: string | null;
  /** El que se dijo en la llamada, si no se reconoce. */
  oido?: string | null;
  vacio?: string;
  /** Su ficha en Clientes › Huéspedes, si se le ha puesto cara. */
  ficha?: string | null;
}) {
  if (confirmado) {
    // Se enlaza cuando el teléfono se ha reconocido en la base de huéspedes.
    // Un nombre corregido a mano también enlaza: la corrección cambia cómo se
    // le llama, no quién es. Los nombres heredados del enriquecimiento antiguo
    // no llevan ficha, y salen sin enlace.
    //
    // El subrayado va SIEMPRE, no solo al pasar por encima. En esta columna
    // conviven nombres que llevan a una ficha y nombres que no, y un enlace que
    // en reposo se ve igual que el texto de al lado no se puede encontrar: hay
    // que pasar el ratón por encima de cada uno para averiguar cuál lleva a algún
    // sitio. Punteado y en gris claro para que la columna no se vuelva ruidosa;
    // sólido y en el color de marca al apuntarlo, que es cuando interesa.
    return ficha ? (
      <Link
        to={ficha}
        onClick={(e) => e.stopPropagation()}
        className="underline decoration-dotted decoration-slate-300 dark:decoration-slate-600
                   underline-offset-[3px] hover:decoration-solid hover:decoration-sereno-dark
                   dark:hover:decoration-white transition-colors"
        title="Ver su ficha de huésped"
      >
        {confirmado}
      </Link>
    ) : (
      <>{confirmado}</>
    );
  }

  if (oido) {
    return (
      <span
        className="inline-flex items-center gap-1 italic text-slate-600 dark:text-slate-300"
        title={`Según lo que se dijo en la llamada, no está fichado en Hostify: «${oido}»`}
      >
        {oido}
        <Info size={12} className="text-sky-500 shrink-0" aria-label="Nombre oído en la llamada" />
      </span>
    );
  }

  return <span className="text-slate-400">{vacio}</span>;
}
